/**
 * A request, from anywhere in the app, that the Billing sheet be opened (kitecorp/transferify-ui#3004).
 *
 * The sheet is a boolean the top bar holds (`nav/normal/NavBar`, ui#1791) with no route behind it, and
 * until now the profile menu's row was its ONE door. The send-form note's "Details and refunds in
 * Billing" link is a second door in a subtree the bar cannot see, so it asks through this store and the
 * bar answers. A module store rather than a context, for the reason `referral/referralDialogSlot` is one:
 * the two sides share nothing but this request, and a context would have to be threaded through the
 * whole shell to reach a note that renders on one panel.
 *
 * <b>A counter, not a flag.</b> Each press increments it, so a second press after the reader closed the
 * sheet is a new request rather than a value the bar already acted on. The bar reads the count through
 * `useSyncExternalStore` and opens the sheet whenever it moves; nothing here knows whether the sheet is
 * open, and nothing here closes it — closing stays the sheet's own business.
 *
 * Plain TypeScript, no React.
 */

let requests = 0;
const listeners = new Set<() => void>();

/** How many times the sheet has been asked for since this page loaded. Stable between changes. */
export function billingSheetRequests(): number {
	return requests;
}

export function subscribeBillingSheetRequests(listener: () => void): () => void {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
}

/** Asks the top bar to open the Billing sheet. Every call is a request of its own. */
export function requestBillingSheet(): void {
	requests += 1;
	for (const listener of [...listeners]) {
		listener();
	}
}

/** Test seam: forgets every request and subscriber. */
export function resetBillingSheetRequests(): void {
	requests = 0;
	listeners.clear();
}
